import { createAdminV2Error } from '@/admin-v2/foundation/errors';

export type AdminV2RuntimePathReadIssue = {
  reason: 'missing' | 'invalid';
  message: string;
  segment: string;
  index: number;
};

export type AdminV2RuntimePathReadResult =
  | { ok: true; value: unknown }
  | ({ ok: false } & AdminV2RuntimePathReadIssue);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function parseArrayIndex(segment: string) {
  if (!/^(0|[1-9]\d*)$/.test(segment)) {
    return null;
  }
  const index = Number(segment);
  return Number.isSafeInteger(index) ? index : null;
}

export function formatAdminV2RuntimePath(path: string[]) {
  return path.length === 0 ? '$' : path.join('.');
}

export function tryReadAdminV2RuntimePath(params: {
  value: unknown;
  path: string[];
  label?: string;
}): AdminV2RuntimePathReadResult {
  let current = params.value;
  const label = params.label ?? 'value';

  for (let index = 0; index < params.path.length; index += 1) {
    const segment = params.path[index];
    if (Array.isArray(current)) {
      const arrayIndex = parseArrayIndex(segment);
      if (arrayIndex === null) {
        return {
          ok: false,
          reason: 'invalid',
          message: `${label} path ${formatAdminV2RuntimePath(params.path)} uses non-numeric array segment ${segment}.`,
          segment,
          index,
        };
      }
      if (arrayIndex >= current.length) {
        return {
          ok: false,
          reason: 'missing',
          message: `${label} path ${formatAdminV2RuntimePath(params.path)} is missing array item ${segment}.`,
          segment,
          index,
        };
      }
      current = current[arrayIndex];
      continue;
    }

    if (!isRecord(current)) {
      return {
        ok: false,
        reason: current === undefined ? 'missing' : 'invalid',
        message: `${label} path ${formatAdminV2RuntimePath(params.path)} cannot read segment ${segment} from ${current === null ? 'null' : typeof current}.`,
        segment,
        index,
      };
    }

    if (!Object.hasOwn(current, segment)) {
      return {
        ok: false,
        reason: 'missing',
        message: `${label} path ${formatAdminV2RuntimePath(params.path)} is missing property ${segment}.`,
        segment,
        index,
      };
    }
    current = current[segment];
  }

  return { ok: true, value: current };
}

export function readAdminV2RuntimePath(params: {
  value: unknown;
  path: string[];
  label?: string;
  fieldPath?: string;
  missingCode?: string;
  invalidCode?: string;
  missingStatus?: number;
  invalidStatus?: number;
}) {
  const result = tryReadAdminV2RuntimePath({
    value: params.value,
    path: params.path,
    label: params.label,
  });
  if (result.ok) {
    return result.value;
  }

  throw createAdminV2Error({
    code:
      result.reason === 'missing'
        ? (params.missingCode ?? 'ADMIN_V2_RUNTIME_PATH_MISSING')
        : (params.invalidCode ?? 'ADMIN_V2_RUNTIME_PATH_INVALID'),
    message: result.message,
    fieldPath: params.fieldPath,
    status:
      result.reason === 'missing'
        ? (params.missingStatus ?? 422)
        : (params.invalidStatus ?? 422),
    details: {
      segment: result.segment,
      path: params.path,
    },
  });
}
